import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import MoverCard from '../components/choose/MoverCard';
import quoteService from '../services/quoteService';
import { API_ENDPOINTS } from '../config/api';

const ChooseMovers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const quoteId = searchParams.get('quoteId') || location.state?.quoteId;
  const originState = location.state?.originState;
  const originZip = location.state?.originZip;
  const form = location.state?.form || {};

  // Fetch movers based on origin state (where user is moving FROM)
  useEffect(() => {
    const fetchMovers = async () => {
      
      if (!originState) {
        setError("No origin state provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await quoteService.getMoversByState(originState, originZip);
        if (result.success) {
          // Filter out paused movers
          const activeMovers = (result.movers || []).filter(mover => !mover.account_paused);
          setMovers(activeMovers);
        } else {
          setError(result.error || "Failed to load movers");
        }
      } catch (err) {
        setError("Failed to load movers");
      } finally {
        setLoading(false);
      }
    };

    fetchMovers();
  }, [originState, originZip]);

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const handleSubmit = async () => {
    if (!quoteId || submitting) return;

    try {
      setSubmitting(true);
      setError("");

      if (selected.length > 0) {
        // Save selected movers
        const saveResult = await quoteService.saveSelectedMovers(quoteId, selected);
        if (!saveResult.success) {
          setError(saveResult.error || "Failed to save selected movers");
          setSubmitting(false);
          return;
        }

        // Create leads for each selected mover (copies all quote data)
        const leadsResult = await quoteService.createLeadsFromQuote(quoteId, selected);
        if (!leadsResult.success) {
          setError(leadsResult.error || "Failed to create leads");
          setSubmitting(false);
          return;
        }
      } else {
        // Create a general lead without specific mover selection
        const leadsResult = await quoteService.createLeadsFromQuote(quoteId, []);
        if (!leadsResult.success) {
          setError(leadsResult.error || "Failed to create lead");
          setSubmitting(false);
          return;
        }
      }

      // Send email notification to user
      try {
        const emailResponse = await fetch(API_ENDPOINTS.SEND_LEAD_EMAIL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userEmail: form.email,
            userName: `${form.firstName} ${form.lastName}`.trim() || 'User',
            quoteDetails: {
              moveType: form.moveType,
              assistanceType: form.assistance,
              from: form.fromAddress ? `${form.fromCity || ''}, ${form.fromState || ''} ${form.fromZip || ''}` : '',
              to: form.toAddress ? `${form.toCity || ''}, ${form.toState || ''} ${form.toZip || ''}` : '',
              moveDate: form.fromDate,
              propertySize: form.assistance === 'Move a Piano' 
                ? `${form.pianoType || 'Piano'}${form.pianoStairs ? ` (${form.pianoStairs} floors)` : ''}`
                : form.assistance === 'Vehicle Move'
                ? `${form.vehicleType || 'Vehicle'} - ${[form.vehicleYear, form.vehicleMake, form.vehicleModel].filter(Boolean).join(' ')}`
                : form.assistance === 'Truck or Container Rental'
                ? (form.truckSize || 'Truck/Container')
                : (form.property || '—'),
              selectedMovers: selected.length
            }
          })
        });
        
        const emailResult = await emailResponse.json();
        if (emailResult.success) {
        } else {
          // Continue anyway - email failure shouldn't stop the flow
        }
      } catch (emailError) {
        // Continue anyway - email failure shouldn't stop the flow
      }

      // Delete quote data after successful lead creation
      const deleteResult = await quoteService.deleteQuoteData(quoteId);
      if (!deleteResult.success) {
        // Continue anyway since leads were created successfully
      }

      // Navigate to success page
      const selectedMovers = movers.filter((m) => selected.includes(m.id));
      navigate('/quote-success', { 
        state: { 
          form, 
          selectedCount: selected.length, 
          movers: selectedMovers,
          quoteId 
        } 
      });
    } catch (err) {
      setError("Failed to submit quote. Please try again.");
      setSubmitting(false);
    }
  };

  const count = selected.length;
  const btnDisabled = submitting;
  const header = useMemo(() => ({
    moveType: form.moveType || 'Residential Move',
    from: form.fromAddress ? `${form.fromCity || ''}, ${form.fromAddress || ''}` : '.',
    to: form.toAddress ? `${form.toCity || ''}, ${form.toAddress || ''}` : '.',
  }), [form]);

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-yellow-50/40 py-8 md:py-12">
      <Helmet>
        <title>Choose Your Movers – MyMoveAdvisor</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-orange-500 text-white rounded-xl w-11 h-11 flex items-center justify-center shadow">🚚</div>
          </div>
          <h1 className="text-center text-xl md:text-2xl font-bold text-gray-900 mb-2">Choose Your Movers</h1>
          <p className="text-center text-gray-600 mb-6">We found professional movers in your area. Select the ones you'd like to receive quotes from.</p>

          <div className="bg-white border border-yellow-200 rounded-xl p-4 md:p-5 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Move Type</div>
                <div className="font-semibold text-gray-800">{header.moveType}</div>
              </div>
              <div>
                <div className="text-gray-500">From</div>
                <div className="font-semibold text-gray-800">{header.from}</div>
              </div>
              <div>
                <div className="text-gray-500">To</div>
                <div className="font-semibold text-gray-800">{header.to}</div>
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <p className="text-gray-600 mt-4">Finding movers in your area...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {!loading && movers.length === 0 && !error && (
            <div className="text-center py-12 bg-white border border-yellow-200 rounded-xl">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-800 font-semibold mb-2">No movers found in your area</p>
              <p className="text-gray-500 text-sm mb-4">We're working on expanding our network to {originState}.</p>
              <p className="text-gray-600 text-sm">You can still submit your request and we'll notify you when movers become available.</p>
            </div>
          )}

          {!loading && movers.length > 0 && (
            <div className="space-y-4">
              {movers.map((m) => (
                <MoverCard 
                  key={m.id} 
                  mover={{
                    id: m.id,
                    name: m.company_name,
                    premium: m.premium || false,
                    rating: m.rating || 0,
                    reviews: m.total_reviews || '0',
                    distance: m.distance || null,
                    city: `${m.city || ''}, ${m.state || ''}`,
                    tags: m.services || [],
                    experience: m.years_in_business ? `${m.years_in_business} years` : 'N/A',
                  }} 
                  selected={selected.includes(m.id)} 
                  onToggle={() => toggle(m.id)} 
                />
              ))}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="text-sm text-gray-600 flex items-center">
              {count === 0 ? 'No movers selected - Request will be saved for review' : `${count} ${count === 1 ? 'mover' : 'movers'} selected`}
            </div>
            <button
              disabled={btnDisabled}
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg py-2.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : count === 0 ? (
                'Submit Request'
              ) : (
                `Request Quotes (${count})`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChooseMovers;
